import React, { useState, useMemo } from 'react'
import {
    CircleHelp,
    Plus,
    GripVertical,
    ChevronRight,
    ChevronDown,
    Pen,
    Trash2,
    FileText,
    Search as SearchIcon,
} from "lucide-react";

const initialCategories = [
    {
        id: "tasks-projects",
        icon: "✅",
        title: "Tasks & Projects",
        description: "Organize your work with tasks, projects, and powerful workflows.",
        isOpen: false,
        pages: [
            {
                id: "untitled-doc",
                title: "Untitled document",
                status: "Draft",
                updatedAt: "Updated Nov 29, 2025",
            },
        ],
    },
    {
        id: "team-collaboration",
        icon: "👥",
        title: "Team Collaboration",
        description: "Work together seamlessly with real-time collaboration features.",
        isOpen: false,
        pages: [],
    },
    {
        id: "getting-started",
        icon: "📘",
        title: "Getting Started",
        description:
            "Learn the basics of Simplifyte and get your first project up and running.",
        isOpen: true,
        pages: [
            {
                id: "quick-start",
                title: "Quick start guide",
                status: "Published",
                updatedAt: "Updated Mar 10, 2024",
            },
            {
                id: "interface",
                title: "Understanding the interface",
                status: "Published",
                updatedAt: "Updated Mar 5, 2024",
            },
            {
                id: "workspace",
                title: "Creating your workspace",
                status: "Published",
                updatedAt: "Updated Feb 14, 2024",
            },
        ],
    },
    {
        id: "integrations",
        icon: "🔌",
        title: "Integrations",
        description: "Connect Simplifyte with your favorite tools and services.",
        isOpen: true,
        pages: [],
    },
    {
        id: "account-billing",
        icon: "💳",
        title: "Account & Billing",
        description:
            "Manage your account settings, subscription, and billing information.",
        isOpen: true,
        pages: [],
    },
    {
        id: "admin-security",
        icon: "🔒",
        title: "Admin & Security",
        description:
            "Enterprise-grade security and administrative controls for your workspace.",
        isOpen: true,
        pages: [],
    },
];

const pageStatusClasses = {
    Published: "bg-[#D1FAE5] text-[#065F46]",
    Draft: "bg-[#FEF3C7] text-[#92400E]",
    Scheduled: "bg-[#EDE9FE] text-[#5B21B6]",
};
export default function AdminTreeView() {
    const [categories, setCategories] = useState(initialCategories);
    const [searchTerm, setSearchTerm] = useState("");

    // ---------- STATS ----------
    const { totalCategories, totalPages, publishedCount, scheduledCount, draftCount } =
        useMemo(() => {
            const allPages = categories.flatMap((c) => c.pages);
            return {
                totalCategories: categories.length,
                totalPages: allPages.length,
                publishedCount: allPages.filter((p) => p.status === "Published").length,
                scheduledCount: allPages.filter((p) => p.status === "Scheduled").length,
                draftCount: allPages.filter((p) => p.status === "Draft").length,
            };
        }, [categories]);
    // ---------- SEARCH ----------
    const filteredCategories = useMemo(() => {
        if (!searchTerm.trim()) return categories;
        const q = searchTerm.toLowerCase();

        return categories.filter((cat) => {
            if (
                cat.title.toLowerCase().includes(q) ||
                cat.description.toLowerCase().includes(q)
            ) {
                return true;
            }
            return cat.pages.some((p) => p.title.toLowerCase().includes(q));
        });
    }, [categories, searchTerm]);

    // ---------- CATEGORY TOGGLE ----------
    const toggleCategoryOpen = (id) => {
        setCategories((prev) =>
            prev.map((cat) =>
                cat.id === id ? { ...cat, isOpen: !cat.isOpen } : cat
            )
        );
    };

    // ---------- CATEGORY DRAG / DROP ----------
    const handleCategoryDragStart = (event, categoryId) => {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/category", categoryId);
    };

    const handleCategoryDrop = (event, targetCategoryId) => {
        event.preventDefault();
        const sourceId = event.dataTransfer.getData("text/category");
        if (!sourceId || sourceId === targetCategoryId) return;

        setCategories((prev) => {
            const updated = [...prev];
            const fromIndex = updated.findIndex((c) => c.id === sourceId);
            const toIndex = updated.findIndex((c) => c.id === targetCategoryId);
            if (fromIndex === -1 || toIndex === -1) return prev;

            const [moved] = updated.splice(fromIndex, 1);
            updated.splice(toIndex, 0, moved);
            return updated;
        });
    };

    const allowDrop = (event) => event.preventDefault();

    // ---------- PAGE DRAG / DROP (within same category) ----------
    const handlePageDragStart = (event, categoryId, pageId) => {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData(
            "text/page",
            JSON.stringify({ categoryId, pageId })
        );
    };

    const handlePageDrop = (event, targetCategoryId, targetPageId) => {
        event.preventDefault();
        const payload = event.dataTransfer.getData("text/page");
        if (!payload) return;

        const { categoryId: sourceCatId, pageId } = JSON.parse(payload);
        if (sourceCatId !== targetCategoryId) return; // no cross-category moves for now

        setCategories((prev) =>
            prev.map((cat) => {
                if (cat.id !== targetCategoryId) return cat;
                const pages = [...cat.pages];
                const fromIndex = pages.findIndex((p) => p.id === pageId);
                const toIndex = pages.findIndex((p) => p.id === targetPageId);
                if (fromIndex === -1 || toIndex === -1) return cat;

                const [movedPage] = pages.splice(fromIndex, 1);
                pages.splice(toIndex, 0, movedPage);
                return { ...cat, pages };
            })
        );
    };

    return (
        <div>
            <div className="max-w-[1470px] mx-auto px-8 py-12">
                {/* HEADER */}
                <div className="flex items-start justify-between mb-10 gap-8">
                    <div className="flex-1">
                        <h1 className="mb-2 text-[#093A5B] text-3xl font-semibold">
                            Documentation
                        </h1>
                        <p className="text-[#6B7280] text-lg">
                            Organize content into categories and create helpful documentation
                        </p>
                    </div>

                    <div className="flex gap-3">
                        <button className="px-5 py-2.5 rounded-xl transition-smooth font-medium bg-transparent text-[#6B7280] hover:bg-[#F3F4F6] border border-[#E5E7EB] flex items-center">
                            <CircleHelp className="w-4 h-4 mr-2" />
                            Help
                        </button>
                        <button className="px-5 py-2.5 rounded-xl transition-smooth font-medium bg-[#2A9CD7] text-white hover:bg-[#2388BD] active:scale-[0.98] flex items-center">
                            <Plus className="w-4 h-4 mr-2" />
                            New Category
                        </button>
                    </div>
                </div>

                {/* STATS */}
                <div className="flex flex-wrap items-center gap-10 mb-10 pb-8 border-b border-[#E5E7EB]">
                    <div>
                        <div className="text-5xl text-[#6B7280] font-semibold mb-2">
                            {totalCategories}
                        </div>
                        <div className="text-[#6B7280] text-sm uppercase tracking-wide">
                            Categories
                        </div>
                    </div>
                    <div>
                        <div className="text-5xl text-[#093A5B] font-semibold mb-2">
                            {totalPages}
                        </div>
                        <div className="text-[#6B7280] text-sm uppercase tracking-wide">
                            Total pages
                        </div>
                    </div>
                    <div>
                        <div className="text-5xl text-[#10B981] font-semibold mb-2">
                            {publishedCount}
                        </div>
                        <div className="text-[#6B7280] text-sm uppercase tracking-wide">
                            Published
                        </div>
                    </div>
                    <div>
                        <div className="text-5xl text-[#8B5CF6] font-semibold mb-2">
                            {scheduledCount}
                        </div>
                        <div className="text-[#6B7280] text-sm uppercase tracking-wide">
                            Scheduled
                        </div>
                    </div>
                    <div>
                        <div className="text-5xl text-[#F59E0B] font-semibold mb-2">
                            {draftCount}
                        </div>
                        <div className="text-[#6B7280] text-sm uppercase tracking-wide">
                            Drafts
                        </div>
                    </div>
                </div>

                {/* SEARCH */}
                <div className="mb-8">
                    <div className="relative max-w-md">
                        <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#6B7280]" />
                        <input
                            type="text"
                            placeholder="Search categories and pages..."
                            className="w-full pl-12 pr-4 py-3 bg-white border border-[#E5E7EB] rounded-xl outline-none focus:ring-2 focus:ring-[#2A9CD7]/50 transition-smooth"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {/* CATEGORIES */}
                <div className="space-y-2">
                    {filteredCategories.map((cat) => {
                        const draftInCat = cat.pages.filter((p) => p.status === "Draft").length;

                        return (
                            <div
                                key={cat.id}
                                className="bg-white border rounded-xl overflow-hidden hover:border-[#2A9CD7]/30 transition-all border-[#E5E7EB]"
                                draggable
                                onDragStart={(e) => handleCategoryDragStart(e, cat.id)}
                                onDragOver={allowDrop}
                                onDrop={(e) => handleCategoryDrop(e, cat.id)}
                            >
                                <div className="group">
                                    {/* CATEGORY HEADER ROW */}
                                    <div className="flex items-center gap-4 p-4 hover:bg-[#F8FAFC] transition-colors">
                                        <div className="opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing">
                                            <GripVertical className="w-5 h-5 text-[#6B7280]" />
                                        </div>

                                        <button
                                            type="button"
                                            className="p-1 hover:bg-[#E5E7EB] rounded transition-colors"
                                            onClick={() => toggleCategoryOpen(cat.id)}
                                        >
                                            {cat.isOpen ? (
                                                <ChevronDown className="w-5 h-5 text-[#6B7280]" />
                                            ) : (
                                                <ChevronRight className="w-5 h-5 text-[#6B7280]" />
                                            )}
                                        </button>

                                        <div className="text-2xl">{cat.icon}</div>

                                        <div className="flex-1 min-w-0">
                                            <div className="flex flex-wrap items-center gap-3">
                                                <h3 className="text-[#093A5B] font-medium">
                                                    {cat.title}
                                                </h3>
                                                <span className="text-[#6B7280] text-sm">
                                                    {cat.pages.length}{" "}
                                                    {cat.pages.length === 1 ? "page" : "pages"}
                                                </span>
                                            </div>
                                            <p className="text-[#6B7280] text-sm mt-1">
                                                {cat.description}
                                            </p>

                                            {draftInCat > 0 && (
                                                <div className="flex items-center gap-2 mt-2">
                                                    <span className="px-2 py-1 bg-[#F59E0B]/10 text-[#F59E0B] text-xs rounded-full font-medium">
                                                        {draftInCat} draft
                                                        {draftInCat > 1 ? "s" : ""}
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button className="p-2 hover:bg-[#E5E7EB] rounded-lg transition-smooth">
                                                <Pen className="w-4 h-4 text-[#6B7280]" />
                                            </button>
                                            <button className="p-2 hover:bg-[#FEF2F2] rounded-lg transition-smooth">
                                                <Trash2 className="w-4 h-4 text-[#EF4444]" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* CATEGORY PAGES */}
                                    {cat.isOpen && (
                                        <div className="bg-[#F8FAFC] border-t border-[#E5E7EB]">
                                            {cat.pages.length > 0 && (
                                                <div className="p-2">
                                                    {cat.pages.map((page, idx) => (
                                                        <div
                                                            key={page.id}
                                                            className="group/doc flex items-center gap-4 px-4 py-3 bg-white rounded-lg mb-2 last:mb-0 hover:shadow-sm transition-all"
                                                            draggable
                                                            onDragStart={(e) =>
                                                                handlePageDragStart(e, cat.id, page.id)
                                                            }
                                                            onDragOver={allowDrop}
                                                            onDrop={(e) =>
                                                                handlePageDrop(e, cat.id, page.id)
                                                            }
                                                        >
                                                            <div className="opacity-0 group-hover/doc:opacity-100 transition-opacity cursor-grab active:cursor-grabbing">
                                                                <GripVertical className="w-4 h-4 text-[#6B7280]" />
                                                            </div>

                                                            <FileText className="w-4 h-4 text-[#6B7280] flex-shrink-0" />

                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex flex-wrap items-center gap-3">
                                                                    <span className="text-[#093A5B] hover:text-[#2A9CD7] transition-colors truncate cursor-pointer">
                                                                        {page.title}
                                                                    </span>
                                                                    <span
                                                                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs ${pageStatusClasses[page.status] ||
                                                                            "bg-gray-100 text-gray-700"
                                                                            }`}
                                                                    >
                                                                        {page.status}
                                                                    </span>
                                                                </div>
                                                                <div className="text-xs text-[#6B7280] mt-1">
                                                                    {page.updatedAt}
                                                                </div>
                                                            </div>

                                                            <div className="flex gap-2 opacity-0 group-hover/doc:opacity-100 transition-opacity">
                                                                <button className="p-2 hover:bg-[#E5E7EB] rounded-lg transition-smooth">
                                                                    <Pen className="w-4 h-4 text-[#6B7280]" />
                                                                </button>
                                                                <button className="p-2 hover:bg-[#FEF2F2] rounded-lg transition-smooth">
                                                                    <Trash2 className="w-4 h-4 text-[#EF4444]" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            <div className="px-4 py-3">
                                                <button className="flex items-center gap-2 text-[#2A9CD7] hover:text-[#2388BD] transition-colors text-sm font-medium">
                                                    <Plus className="w-4 h-4" />
                                                    Add page to this category
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    )
}
